// components/billings/invoice-builder.component.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

import { createPortal } from "react-dom";

import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useSelector } from "react-redux";

import CurrencyPicker from "./currency-picker.component";

import { CURRENCIES, COMMON_CURRENCY_CODES } from "../../constants/currencies";

import {
  FiArrowLeft,
  FiChevronRight,
  FiPrinter,
  FiSave,
  FiTrash2,
  FiUpload,
  FiX,
} from "react-icons/fi";

import { db, fieldValue } from "../../firebase/firebase.utils";
import uploadFile from "../../utils/uploadFile";

import "./invoice-builder.styles.scss";

const TEMPLATES = [
  { id: "minimal", label: "Minimal" },
  { id: "boxed", label: "Boxed" },
  { id: "clean", label: "Clean" },
];

const UNIT_OPTIONS = [
  { value: "hour", label: "Hours" },
  { value: "qty", label: "QTY" },
  { value: "day", label: "Days" },
  { value: "fixed", label: "Fixed" },
];

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

function safeNumberText(value) {
  const cleaned = String(value)
    .replace(",", ".")
    .replace(/[^0-9.]/g, "");

  const parts = cleaned.split(".");

  if (parts.length <= 2) {
    return cleaned;
  }

  return `${parts[0]}.${parts.slice(1).join("")}`;
}

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function multilineHtml(value = "") {
  return escapeHtml(value).replace(/\n/g, "<br />");
}

function getUnitLabel(unit) {
  return UNIT_OPTIONS.find((option) => option.value === unit)?.label || "Hours";
}

function getCurrencySymbol(currencyCode = "USD") {
  const code = String(currencyCode || "USD").toUpperCase();

  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);

    return parts.find((part) => part.type === "currency")?.value || code;
  } catch {
    return code;
  }
}

function formatMoney(currencyCode, amount) {
  const code = String(currencyCode || "USD").toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  } catch {
    return `${code} ${Number(amount || 0).toFixed(2)}`;
  }
}

function toDateInputValue(value) {
  if (!value) return "";

  let date;

  if (typeof value.toDate === "function") {
    date = value.toDate();
  } else if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function fromDateInputValue(value) {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function renderInvoiceTemplateHtml({ templateId, data, isThumbnail = false }) {
  const rows = data.rows || [];

  const total = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  const pagePadding = isThumbnail ? 10 : 32;
  const fontSize = isThumbnail ? 9 : 13;
  const titleSize = isThumbnail ? 17 : 28;
  const logoHeight = isThumbnail ? 22 : 48;

  const templateCss =
    templateId === "boxed"
      ? `
        .invoice-wrap {
          border: 1px solid #d9d9d9;
          border-radius: 14px;
          padding: ${isThumbnail ? 10 : 24}px;
        }

        th {
          background: #f1f1f1;
        }
      `
      : templateId === "clean"
        ? `
          .invoice-wrap {
            border-top: 6px solid #252525;
            padding-top: ${isThumbnail ? 10 : 20}px;
          }

          .invoice-title {
            letter-spacing: 1.5px;
            text-transform: uppercase;
          }

          th {
            color: #666;
            background: transparent;
            border-bottom: 2px solid #252525;
          }
        `
        : `
          .invoice-wrap {
            padding-top: 4px;
          }

          th {
            background: #fafafa;
          }
        `;

  const logoHtml = data.logoUrl
    ? `
        <img
          class="invoice-logo"
          src="${escapeHtml(data.logoUrl)}"
          alt="Business logo"
        />
      `
    : "";

  const fromAddressHtml = data.fromAddress
    ? `<div class="muted">${multilineHtml(data.fromAddress)}</div>`
    : "";

  const toAddressHtml = data.toAddress
    ? `<div class="muted">${multilineHtml(data.toAddress)}</div>`
    : "";

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />

        <title>${escapeHtml(data.invoiceNumber || "Invoice")}</title>

        <style>
          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #222222;
            font-family: Arial, Helvetica, sans-serif;
          }

          body {
            padding: ${pagePadding}px;
          }

          ${templateCss}

          .top-row,
          .party-row {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 24px;
          }

          .invoice-title {
            margin: 0 0 5px;
            font-size: ${titleSize}px;
            font-weight: 800;
          }

          .invoice-logo {
            display: block;
            max-width: ${isThumbnail ? 90 : 180}px;
            height: ${logoHeight}px;
            object-fit: contain;
          }

          .muted {
            color: #666666;
            font-size: ${fontSize}px;
            line-height: 1.5;
          }

          .party-row,
          .invoice-card {
            margin-top: ${isThumbnail ? 10 : 22}px;
            padding-top: ${isThumbnail ? 10 : 18}px;
            border-top: 1px solid #eeeeee;
          }

          .party {
            width: 48%;
            font-size: ${fontSize}px;
            line-height: 1.5;
          }

          .party-label {
            margin-bottom: 4px;
            color: #777777;
            font-size: ${isThumbnail ? 8 : 11}px;
            font-weight: 700;
            text-transform: uppercase;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          th,
          td {
            padding: ${isThumbnail ? 6 : 11}px 8px;
            border-bottom: 1px solid #eeeeee;
            font-size: ${fontSize}px;
            text-align: left;
          }

          th {
            font-weight: 700;
          }

          .right {
            text-align: right;
          }

          .total-row td {
            padding-top: ${isThumbnail ? 9 : 16}px;
            border-bottom: none;
            font-size: ${isThumbnail ? 10 : 15}px;
            font-weight: 800;
          }

          .notes {
            margin-top: ${isThumbnail ? 10 : 22}px;
            padding-top: ${isThumbnail ? 10 : 18}px;
            border-top: 1px solid #eeeeee;
            color: #555555;
            font-size: ${fontSize}px;
            line-height: 1.55;
          }

          @media print {
            body {
              padding: 0;
            }

            .invoice-wrap {
              break-inside: avoid;
            }
          }
        </style>
      </head>

      <body>
        <main class="invoice-wrap">
          <div class="top-row">
            <div>
              <h1 class="invoice-title">Invoice</h1>

              <div class="muted">
                #${escapeHtml(data.invoiceNumber)}
              </div>

              <div class="muted">
                Issued: ${escapeHtml(data.issueDate)}
                ${data.dueDate ? ` • Due: ${escapeHtml(data.dueDate)}` : ""}
              </div>
            </div>

            <div>${logoHtml}</div>
          </div>

          <div class="party-row">
            <div class="party">
              <div class="party-label">From</div>

              <strong>${escapeHtml(data.fromName)}</strong>

              ${
                data.fromEmail
                  ? `<div class="muted">${escapeHtml(data.fromEmail)}</div>`
                  : ""
              }

              ${fromAddressHtml}
            </div>

            <div class="party">
              <div class="party-label">To</div>

              <strong>${escapeHtml(data.toName)}</strong>

              ${
                data.toEmail
                  ? `<div class="muted">${escapeHtml(data.toEmail)}</div>`
                  : ""
              }

              ${toAddressHtml}
            </div>
          </div>

          <div class="invoice-card">
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="right">Qty</th>
                  <th class="right">Unit</th>
                  <th class="right">Rate</th>
                  <th class="right">Amount</th>
                </tr>
              </thead>

              <tbody>
                ${rows
                  .map(
                    (row) => `
                      <tr>
                        <td>${escapeHtml(row.desc)}</td>
                        <td class="right">
                          ${Number(row.qty || 0).toFixed(2)}
                        </td>
                        <td class="right">
                          ${escapeHtml(getUnitLabel(row.unit))}
                        </td>
                        <td class="right">
                          ${escapeHtml(
                            formatMoney(data.currencyCode, row.rate),
                          )}
                        </td>
                        <td class="right">
                          ${escapeHtml(
                            formatMoney(data.currencyCode, row.amount),
                          )}
                        </td>
                      </tr>
                    `,
                  )
                  .join("")}

                <tr class="total-row">
                  <td></td>
                  <td></td>
                  <td></td>
                  <td class="right">Total</td>

                  <td class="right">
                    ${escapeHtml(formatMoney(data.currencyCode, total))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          ${
            data.notes?.trim()
              ? `
                <div class="notes">
                  ${multilineHtml(data.notes)}
                </div>
              `
              : ""
          }
        </main>
      </body>
    </html>
  `;
}

function printInvoiceWindow(printWindow, html) {
  if (!printWindow) return;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  const images = Array.from(printWindow.document.querySelectorAll("img"));

  const waitForImages = images.map(
    (image) =>
      new Promise((resolve) => {
        if (image.complete) {
          resolve();
          return;
        }

        image.onload = resolve;
        image.onerror = resolve;
      }),
  );

  Promise.all(waitForImages).finally(() => {
    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  });
}

/* -------------------------------------------------------------------------- */
/*                              Main component                                */
/* -------------------------------------------------------------------------- */

export default function InvoiceBuilder({ mode = "create" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { wspaceid, invoiceId } = useParams();
  const [searchParams] = useSearchParams();

  const currentUser = useSelector((state) => state.user.currentUser);

  const fileInputRef = useRef(null);
  const initialClientAppliedRef = useRef(false);

  const isEditing = mode === "edit" && Boolean(invoiceId);
  const initialClientId = searchParams.get("clientId");

  const defaultInvoiceNumber = useMemo(
    () => `INV-${String(Date.now()).slice(-6)}`,
    [],
  );

  const [step, setStep] = useState(0);

  const [spaceInfo, setSpaceInfo] = useState(null);
  const [clients, setClients] = useState([]);

  const [loadingInvoice, setLoadingInvoice] = useState(isEditing);

  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const [invoiceNumber, setInvoiceNumber] = useState(defaultInvoiceNumber);

  const [issueDate, setIssueDate] = useState(() =>
    toDateInputValue(new Date()),
  );

  const [dueDate, setDueDate] = useState("");
  const [currencyCode, setCurrencyCode] = useState("USD");

  const [selectedClientId, setSelectedClientId] = useState("");

  const [fromName, setFromName] = useState("Antlogy");
  const [fromEmail, setFromEmail] = useState(currentUser?.email || "");
  const [fromAddress, setFromAddress] = useState("");

  const [toName, setToName] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [toAddress, setToAddress] = useState("");

  const [items, setItems] = useState([
    {
      desc: "Work session",
      qty: "1.5",
      unit: "hour",
      rate: "60",
    },
    {
      desc: "Meeting",
      qty: "0.5",
      unit: "hour",
      rate: "40",
    },
  ]);

  const [notes, setNotes] = useState("Thank you for your business.");

  const [logos, setLogos] = useState([]);
  const [useLogo, setUseLogo] = useState(false);
  const [selectedLogoUrl, setSelectedLogoUrl] = useState("");

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);
  const logoBusy = useRef(false);

  const [templateId, setTemplateId] = useState("minimal");

  const [previewOpen, setPreviewOpen] = useState(false);

  const [sent, setSent] = useState(searchParams.get("sent") === "true");

  const [paid, setPaid] = useState(searchParams.get("paid") === "true");

  const selectedCurrency = useMemo(
    () => CURRENCIES.find((currency) => currency.code === currencyCode) || null,
    [currencyCode],
  );

  const currencySymbol =
    selectedCurrency?.symbol || getCurrencySymbol(currencyCode);

  const closeBuilder = () => {
    if (location.state?.backgroundPath) {
      navigate(-1);
      return;
    }

    navigate(`/s/${wspaceid}`, {
      replace: true,
    });
  };

  /* ------------------------------- Modal UX ------------------------------- */

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (previewOpen) {
          setPreviewOpen(false);
        } else {
          closeBuilder();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewOpen]);

  /* ---------------------------- Workspace data ---------------------------- */

  useEffect(() => {
    if (!wspaceid) return undefined;

    const unsubscribe = db
      .collection("space")
      .doc(wspaceid)
      .onSnapshot(
        (snapshot) => {
          if (!snapshot.exists) {
            setLoadError("This workspace could not be found.");
            return;
          }

          const data = {
            id: snapshot.id,
            ...snapshot.data(),
          };

          setSpaceInfo(data);

          if (!isEditing) {
            setFromName((currentValue) => {
              if (!currentValue || currentValue === "Antlogy") {
                return data.name || "Antlogy";
              }

              return currentValue;
            });
          }
        },
        (error) => {
          console.error("Workspace listener error:", error);
          setLoadError("Workspace information could not be loaded.");
        },
      );

    return unsubscribe;
  }, [wspaceid, isEditing]);

  /* ------------------------------- Clients -------------------------------- */

  useEffect(() => {
    if (!wspaceid) return undefined;

    const unsubscribe = db
      .collection("space")
      .doc(wspaceid)
      .collection("clients")
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.docs
            .map((document) => ({
              ...document.data(),
              id: document.id,
            }))
            .filter((client) => !client.archived)
            .sort((clientA, clientB) =>
              String(clientA.name || "").localeCompare(
                String(clientB.name || ""),
              ),
            );

          setClients(data);
        },
        (error) => {
          console.error("Clients listener error:", error);
          setLoadError("Clients could not be loaded.");
        },
      );

    return unsubscribe;
  }, [wspaceid]);

  useEffect(() => {
    if (
      isEditing ||
      !initialClientId ||
      !clients.length ||
      initialClientAppliedRef.current
    ) {
      return;
    }

    const client = clients.find((item) => item.id === initialClientId);

    if (!client) return;

    initialClientAppliedRef.current = true;

    setSelectedClientId(client.id);
    setToName(client.name || "");
    setToEmail(client.email || "");
    setToAddress(client.address || "");
  }, [clients, initialClientId, isEditing]);

  const handleClientChange = (clientId) => {
    setSelectedClientId(clientId);

    const client = clients.find((item) => item.id === clientId);

    if (!client) {
      setToName("");
      setToEmail("");
      setToAddress("");
      return;
    }

    setToName(client.name || "");
    setToEmail(client.email || "");
    setToAddress(client.address || "");
  };

  /* ------------------------- Load existing invoice ------------------------ */

  useEffect(() => {
    if (!isEditing || !wspaceid || !invoiceId) {
      setLoadingInvoice(false);
      return;
    }

    let active = true;

    const loadInvoice = async () => {
      try {
        setLoadingInvoice(true);
        setLoadError("");

        const snapshot = await db
          .collection("space")
          .doc(wspaceid)
          .collection("invoices")
          .doc(invoiceId)
          .get();

        if (!snapshot.exists) {
          throw new Error("This invoice could not be found.");
        }

        if (!active) return;

        const invoice = snapshot.data();

        setInvoiceNumber(invoice.invoiceNumber || defaultInvoiceNumber);

        setIssueDate(
          toDateInputValue(invoice.issueDate) || toDateInputValue(new Date()),
        );

        setDueDate(toDateInputValue(invoice.dueDate));

        setCurrencyCode(String(invoice.currencyCode || "USD").toUpperCase());

        setSelectedClientId(invoice.clientId || "");

        setFromName(invoice?.from?.name || spaceInfo?.name || "Antlogy");

        setFromEmail(invoice?.from?.email || currentUser?.email || "");

        setFromAddress(invoice?.from?.address || "");

        setToName(invoice?.to?.name || invoice.clientName || "");

        setToEmail(invoice?.to?.email || "");
        setToAddress(invoice?.to?.address || "");

        setItems(
          Array.isArray(invoice.items)
            ? invoice.items.map((item) => ({
                desc: item.desc || "",
                qty: String(item.qty ?? ""),
                unit: item.unit || "hour",
                rate: String(item.rate ?? ""),
              }))
            : [],
        );

        setNotes(invoice.notes || "");
        setTemplateId(invoice.templateId || "minimal");

        setSent(Boolean(invoice.sent));
        setPaid(Boolean(invoice.paid));

        if (invoice.logoUrl) {
          setUseLogo(true);
          setSelectedLogoUrl(invoice.logoUrl);
        } else {
          setUseLogo(false);
          setSelectedLogoUrl("");
        }
      } catch (error) {
        console.error("Load invoice error:", error);
        setLoadError(error?.message || "The invoice could not be loaded.");
      } finally {
        if (active) {
          setLoadingInvoice(false);
        }
      }
    };

    loadInvoice();

    return () => {
      active = false;
    };
  }, [isEditing, wspaceid, invoiceId, defaultInvoiceNumber]);

  /* ---------------------------- Workspace logos --------------------------- */

  useEffect(() => {
    if (!wspaceid) return undefined;

    const unsubscribe = db
      .collection("space")
      .doc(wspaceid)
      .collection("assets_logos")
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((document) => ({
            ...document.data(),
            id: document.id,
          }));

          setLogos(data);
        },
        (error) => {
          console.error("Logo listener error:", error);
        },
      );

    return unsubscribe;
  }, [wspaceid]);

  useEffect(() => {
    if (useLogo && !selectedLogoUrl && logos[0]?.url) {
      setSelectedLogoUrl(logos[0].url);
    }
  }, [useLogo, selectedLogoUrl, logos]);

  /* ------------------------------ Line items ------------------------------ */

  const updateItem = (index, patch) => {
    setItems((currentItems) =>
      currentItems.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              ...patch,
            }
          : item,
      ),
    );
  };

  const addItem = () => {
    setItems((currentItems) => [
      ...currentItems,
      {
        desc: "New item",
        qty: "1",
        unit: "hour",
        rate: "50",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems((currentItems) =>
      currentItems.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const totals = useMemo(() => {
    const hours = items.reduce((sum, item) => {
      if (item.unit !== "hour") return sum;

      return sum + Number(item.qty || 0);
    }, 0);

    const subtotal = items.reduce(
      (sum, item) => sum + Number(item.qty || 0) * Number(item.rate || 0),
      0,
    );

    return {
      hours,
      subtotal,
    };
  }, [items]);

  /* ------------------------------- Preview -------------------------------- */

  const templateData = useMemo(() => {
    const rows = items.map((item) => {
      const qty = Number(item.qty || 0);
      const rate = Number(item.rate || 0);

      return {
        desc: item.desc || "",
        qty,
        unit: item.unit || "hour",
        rate,
        amount: qty * rate,
      };
    });

    return {
      invoiceNumber,
      issueDate,
      dueDate,
      currencyCode,
      currencySymbol,
      fromName,
      fromEmail,
      fromAddress,
      toName,
      toEmail,
      toAddress,
      rows,
      notes,
      logoUrl: useLogo && selectedLogoUrl ? selectedLogoUrl : "",
    };
  }, [
    invoiceNumber,
    issueDate,
    dueDate,
    currencyCode,
    currencySymbol,
    fromName,
    fromEmail,
    fromAddress,
    toName,
    toEmail,
    toAddress,
    items,
    notes,
    useLogo,
    selectedLogoUrl,
  ]);

  const previewHtml = useMemo(
    () =>
      renderInvoiceTemplateHtml({
        templateId,
        data: templateData,
        isThumbnail: false,
      }),
    [templateId, templateData],
  );

  /* ------------------------------ Validation ------------------------------ */

  const getStepError = (stepIndex) => {
    if (stepIndex === 0) {
      if (!invoiceNumber.trim()) {
        return "Please enter an invoice number.";
      }

      if (!issueDate) {
        return "Please select an issue date.";
      }

      if (!selectedClientId) {
        return "Please select a client.";
      }

      if (!fromName.trim() || !toName.trim()) {
        return "Please enter both sender and client names.";
      }

      if (
        dueDate &&
        fromDateInputValue(dueDate) < fromDateInputValue(issueDate)
      ) {
        return "The due date cannot be earlier than the issue date.";
      }
    }

    if (stepIndex === 1) {
      if (!items.length) {
        return "Please add at least one line item.";
      }

      if (items.some((item) => !String(item.desc || "").trim())) {
        return "Every line item needs a description.";
      }
    }

    return "";
  };

  const goToStep = (nextStep) => {
    if (nextStep > step) {
      const currentStepError = getStepError(step);

      if (currentStepError) {
        window.alert(currentStepError);
        return;
      }
    }

    setStep(nextStep);
  };

  const next = () => {
    const error = getStepError(step);

    if (error) {
      window.alert(error);
      return;
    }

    if (step < 3) {
      setStep((currentStep) => currentStep + 1);
    }
  };

  const back = () => {
    if (step > 0) {
      setStep((currentStep) => currentStep - 1);
      return;
    }

    closeBuilder();
  };

  /* ------------------------------ Logo upload ----------------------------- */

  const uploadLogo = async (file) => {
    if (!file || !currentUser?.uid || !wspaceid || logoBusy.current) return;

    if (!file.type.startsWith("image/")) {
      window.alert("Please select an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      window.alert("Please select an image smaller than 5 MB.");
      return;
    }

    logoBusy.current = true;
    try {
      setUploadingLogo(true);
      setLogoProgress(0);

      const cleanFileName = file.name
        .replace(/[^a-zA-Z0-9._-]/g, "-")
        .toLowerCase();

      const folderId = spaceInfo?.spaceFolderId || wspaceid;

      const path = `space/${folderId}/logos/logo-${Date.now()}-${cleanFileName}`;

      const { url } = await uploadFile({
        file, fullPath: path, userId: currentUser.uid, parent: `space/${wspaceid}`,
        contentType: file.type,
        onProgress: ({ progress }) => setLogoProgress(Math.round(progress * 100)),
      });

      await db
        .collection("space")
        .doc(wspaceid)
        .collection("assets_logos")
        .add({
          url,
          path,
          createdAt: fieldValue.serverTimestamp(),
          createdBy: currentUser?.uid || null,
        });

      setUseLogo(true);
      setSelectedLogoUrl(url);
    } catch (error) {
      console.error("Logo upload error:", error);

      window.alert(error?.message || "The logo could not be uploaded.");
    } finally {
      logoBusy.current = false;
      setUploadingLogo(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  /* -------------------------------- Saving -------------------------------- */

  const saveInvoice = async ({ printAfterSave = false } = {}) => {
    const basicsError = getStepError(0);
    const itemsError = getStepError(1);
    const validationError = basicsError || itemsError;

    if (validationError) {
      window.alert(validationError);
      return;
    }

    let printWindow = null;

    if (printAfterSave) {
      printWindow = window.open("", "_blank");

      if (printWindow) {
        printWindow.opener = null;

        printWindow.document.write(`
          <!doctype html>
          <html>
            <body style="font-family: Arial; padding: 30px;">
              Preparing invoice…
            </body>
          </html>
        `);

        printWindow.document.close();
      }
    }

    try {
      setSaving(true);

      const payload = {
        spaceId: wspaceid,
        clientId: selectedClientId,
        clientName: toName.trim(),

        invoiceNumber: invoiceNumber.trim(),

        issueDate: fromDateInputValue(issueDate),
        dueDate: dueDate ? fromDateInputValue(dueDate) : null,

        currencyCode: currencyCode.trim().toUpperCase(),

        from: {
          name: fromName.trim(),
          email: fromEmail.trim(),
          address: fromAddress.trim(),
        },

        to: {
          name: toName.trim(),
          email: toEmail.trim(),
          address: toAddress.trim(),
        },

        items: items.map((item) => ({
          desc: String(item.desc || "").trim(),
          qty: Number(item.qty || 0),
          unit: item.unit || "hour",
          rate: Number(item.rate || 0),
        })),

        notes: notes.trim(),
        templateId,

        logoUrl: useLogo && selectedLogoUrl ? selectedLogoUrl : null,

        totals: {
          hours: totals.hours,
          subtotal: totals.subtotal,
        },

        updatedAt: fieldValue.serverTimestamp(),
        lastUpdater: currentUser?.uid || null,

        sent,
        paid,
      };

      const invoicesRef = db
        .collection("space")
        .doc(wspaceid)
        .collection("invoices");

      if (isEditing) {
        await invoicesRef.doc(invoiceId).update(payload);
      } else {
        await invoicesRef.add({
          ...payload,
          createdAt: fieldValue.serverTimestamp(),
          createdBy: currentUser?.uid || null,
        });
      }

      if (printAfterSave && printWindow) {
        printInvoiceWindow(printWindow, previewHtml);
      } else if (printAfterSave && !printWindow) {
        window.alert(
          "The invoice was saved, but your browser blocked the print window.",
        );
      } else {
        window.alert("Invoice saved.");
      }

      closeBuilder();
    } catch (error) {
      console.error("Save invoice error:", error);

      if (printWindow) {
        printWindow.close();
      }

      window.alert(error?.message || "The invoice could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  /* ----------------------------- Loading state ---------------------------- */

  const modalContent = (
    <div
      className="invoice-builder__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          closeBuilder();
        }
      }}
    >
      <section
        className="invoice-builder__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-builder-title"
      >
        <header className="invoice-builder__header">
          <div className="invoice-builder__header-left">
            <button
              type="button"
              className="invoice-builder__icon-button"
              onClick={back}
              aria-label={
                step === 0 ? "Close invoice builder" : "Previous step"
              }
            >
              <FiArrowLeft />
            </button>

            <div
              className="invoice-builder__space-icon"
              style={{
                background: spaceInfo?.color || "#dddddd",
              }}
            >
              {spaceInfo?.name?.charAt(0) || "?"}
            </div>

            <FiChevronRight className="invoice-builder__breadcrumb-icon" />
          </div>

          <div className="invoice-builder__heading">
            <h2 id="invoice-builder-title">
              {isEditing ? "Edit Invoice" : "Invoice Builder"}
            </h2>

            <span>Step {step + 1} of 4</span>
          </div>

          <button
            type="button"
            className="invoice-builder__icon-button"
            onClick={closeBuilder}
            aria-label="Close invoice builder"
          >
            <FiX />
          </button>
        </header>

        <nav className="invoice-builder__steps" aria-label="Invoice steps">
          {["Basics", "Line items", "Branding", "Template"].map(
            (label, index) => (
              <button
                type="button"
                key={label}
                className={
                  index === step
                    ? "invoice-builder__step invoice-builder__step--active"
                    : index < step
                      ? "invoice-builder__step invoice-builder__step--complete"
                      : "invoice-builder__step"
                }
                onClick={() => goToStep(index)}
              >
                <span>{index + 1}</span>
                {label}
              </button>
            ),
          )}
        </nav>

        <main className="invoice-builder__content">
          {loadingInvoice ? (
            <div className="invoice-builder__centered">Loading invoice…</div>
          ) : loadError ? (
            <div className="invoice-builder__error">{loadError}</div>
          ) : (
            <>
              {step === 0 && (
                <div className="invoice-builder__page">
                  <SectionTitle title="Invoice basics" />

                  <div className="invoice-builder__form-grid">
                    <Field label="Invoice number">
                      <input
                        value={invoiceNumber}
                        onChange={(event) =>
                          setInvoiceNumber(event.target.value)
                        }
                        placeholder="Invoice number"
                      />
                    </Field>

                    <Field label="Client">
                      <select
                        value={selectedClientId}
                        onChange={(event) =>
                          handleClientChange(event.target.value)
                        }
                      >
                        <option value="">Select a client</option>

                        {selectedClientId &&
                          !clients.some(
                            (client) => client.id === selectedClientId,
                          ) && (
                            <option value={selectedClientId}>
                              {toName || "Current client"}
                            </option>
                          )}

                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.name || "Unnamed client"}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Issue date">
                      <input
                        type="date"
                        value={issueDate}
                        onChange={(event) => setIssueDate(event.target.value)}
                      />
                    </Field>

                    <Field label="Due date (optional)">
                      <input
                        type="date"
                        value={dueDate}
                        min={issueDate || undefined}
                        onChange={(event) => setDueDate(event.target.value)}
                      />
                    </Field>

                    <div className="invoice-builder__currency-field">
                      <CurrencyPicker
                        value={currencyCode}
                        onChange={(currency) => setCurrencyCode(currency.code)}
                        currencies={CURRENCIES}
                        commonCurrencyCodes={COMMON_CURRENCY_CODES}
                        label="Currency"
                      />
                    </div>
                  </div>

                  <SectionTitle title="From" />

                  <div className="invoice-builder__form-grid">
                    <Field label="Name">
                      <input
                        value={fromName}
                        onChange={(event) => setFromName(event.target.value)}
                      />
                    </Field>

                    <Field label="Email">
                      <input
                        type="email"
                        value={fromEmail}
                        onChange={(event) => setFromEmail(event.target.value)}
                      />
                    </Field>

                    <Field label="Address (optional)" wide>
                      <textarea
                        value={fromAddress}
                        onChange={(event) => setFromAddress(event.target.value)}
                      />
                    </Field>
                  </div>

                  <SectionTitle title="To" />

                  <div className="invoice-builder__form-grid">
                    <Field label="Client name">
                      <input
                        value={toName}
                        onChange={(event) => setToName(event.target.value)}
                      />
                    </Field>

                    <Field label="Client email">
                      <input
                        type="email"
                        value={toEmail}
                        onChange={(event) => setToEmail(event.target.value)}
                      />
                    </Field>

                    <Field label="Client address (optional)" wide>
                      <textarea
                        value={toAddress}
                        onChange={(event) => setToAddress(event.target.value)}
                      />
                    </Field>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="invoice-builder__page">
                  <div className="invoice-builder__title-row">
                    <SectionTitle title="Line items" />

                    <button
                      type="button"
                      className="invoice-builder__secondary-button"
                      onClick={addItem}
                    >
                      Add item
                    </button>
                  </div>

                  <div className="invoice-items">
                    <div className="invoice-items__header">
                      <span>Description</span>
                      <span>Quantity</span>
                      <span>Unit</span>
                      <span>Rate</span>
                      <span>Amount</span>
                      <span></span>
                    </div>

                    {items.map((item, index) => {
                      const amount =
                        Number(item.qty || 0) * Number(item.rate || 0);

                      return (
                        <div
                          className="invoice-items__row"
                          key={`invoice-item-${index}`}
                        >
                          <input
                            value={item.desc}
                            onChange={(event) =>
                              updateItem(index, {
                                desc: event.target.value,
                              })
                            }
                            placeholder="Description"
                          />

                          <input
                            inputMode="decimal"
                            value={item.qty}
                            onChange={(event) =>
                              updateItem(index, {
                                qty: safeNumberText(event.target.value),
                              })
                            }
                          />

                          <select
                            value={item.unit}
                            onChange={(event) =>
                              updateItem(index, {
                                unit: event.target.value,
                              })
                            }
                          >
                            {UNIT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>

                          <input
                            inputMode="decimal"
                            value={item.rate}
                            onChange={(event) =>
                              updateItem(index, {
                                rate: safeNumberText(event.target.value),
                              })
                            }
                          />

                          <strong>{formatMoney(currencyCode, amount)}</strong>

                          <button
                            type="button"
                            className="invoice-items__delete"
                            onClick={() => removeItem(index)}
                            aria-label={`Remove item ${index + 1}`}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {!items.length && (
                    <div className="invoice-builder__empty">
                      No line items yet.
                    </div>
                  )}

                  <div className="invoice-builder__totals">
                    <span>
                      Total hours: <strong>{totals.hours.toFixed(2)}</strong>
                    </span>

                    <span>
                      Subtotal:{" "}
                      <strong>
                        {formatMoney(currencyCode, totals.subtotal)}
                      </strong>
                    </span>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="invoice-builder__page">
                  <SectionTitle title="Branding & notes" />

                  <div className="invoice-builder__card">
                    <strong>Include a business logo?</strong>

                    <div className="invoice-builder__choice-row">
                      <button
                        type="button"
                        className={
                          !useLogo
                            ? "invoice-builder__choice invoice-builder__choice--active"
                            : "invoice-builder__choice"
                        }
                        onClick={() => {
                          setUseLogo(false);
                          setSelectedLogoUrl("");
                        }}
                      >
                        No logo
                      </button>

                      <button
                        type="button"
                        className={
                          useLogo
                            ? "invoice-builder__choice invoice-builder__choice--active"
                            : "invoice-builder__choice"
                        }
                        onClick={() => setUseLogo(true)}
                      >
                        Use logo
                      </button>
                    </div>

                    {useLogo && (
                      <>
                        <div className="invoice-logos">
                          {logos.map((logo) => (
                            <button
                              type="button"
                              key={logo.id}
                              className={
                                selectedLogoUrl === logo.url
                                  ? "invoice-logos__item invoice-logos__item--selected"
                                  : "invoice-logos__item"
                              }
                              onClick={() => setSelectedLogoUrl(logo.url)}
                            >
                              <img src={logo.url} alt="Workspace logo" />
                            </button>
                          ))}

                          <button
                            type="button"
                            className="invoice-logos__upload"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingLogo}
                          >
                            <FiUpload />

                          {uploadingLogo ? (logoProgress === 100 ? "Saving…" : `Uploading ${logoProgress}%`) : "Upload"}
                          </button>

                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(event) =>
                              uploadLogo(event.target.files?.[0])
                            }
                          />
                        </div>

                        <small className="invoice-builder__hint">
                          {selectedLogoUrl
                            ? "Selected logo ✓"
                            : "Choose an existing logo or upload one."}
                        </small>
                      </>
                    )}
                  </div>

                  <Field label="Notes" wide>
                    <textarea
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      rows={5}
                    />
                  </Field>
                </div>
              )}

              {step === 3 && (
                <div className="invoice-builder__page">
                  <SectionTitle title="Choose a template" />

                  <div className="invoice-templates">
                    {TEMPLATES.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        selected={templateId === template.id}
                        data={templateData}
                        onClick={() => {
                          setTemplateId(template.id);
                          setPreviewOpen(true);
                        }}
                      />
                    ))}
                  </div>

                  <div className="invoice-builder__card">
                    <strong>Invoice status</strong>

                    <div className="invoice-builder__status-row">
                      <label>
                        <input
                          type="checkbox"
                          checked={sent}
                          onChange={(event) => setSent(event.target.checked)}
                        />
                        Marked as sent
                      </label>

                      <label>
                        <input
                          type="checkbox"
                          checked={paid}
                          onChange={(event) => setPaid(event.target.checked)}
                        />
                        Marked as paid
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </main>

        {!loadingInvoice && !loadError && (
          <footer className="invoice-builder__footer">
            <button
              type="button"
              className="invoice-builder__secondary-button"
              onClick={back}
              disabled={saving}
            >
              {step === 0 ? "Close" : "Back"}
            </button>

            {step < 3 ? (
              <button
                type="button"
                className="invoice-builder__primary-button"
                onClick={next}
              >
                Next
              </button>
            ) : (
              <div className="invoice-builder__save-actions">
                <button
                  type="button"
                  className="invoice-builder__secondary-button"
                  onClick={() => saveInvoice()}
                  disabled={saving}
                >
                  <FiSave />

                  {saving ? "Saving…" : "Save invoice"}
                </button>

                <button
                  type="button"
                  className="invoice-builder__primary-button"
                  onClick={() =>
                    saveInvoice({
                      printAfterSave: true,
                    })
                  }
                  disabled={saving}
                >
                  <FiPrinter />

                  {saving ? "Saving…" : "Save & Print / PDF"}
                </button>
              </div>
            )}
          </footer>
        )}

        {previewOpen && (
          <div
            className="invoice-preview__backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setPreviewOpen(false);
              }
            }}
          >
            <section className="invoice-preview__dialog">
              <header>
                <strong>
                  Preview:{" "}
                  {TEMPLATES.find((template) => template.id === templateId)
                    ?.label || "Template"}
                </strong>

                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  aria-label="Close preview"
                >
                  <FiX />
                </button>
              </header>

              <iframe title="Invoice preview" srcDoc={previewHtml} />

              <footer>
                <button
                  type="button"
                  className="invoice-builder__primary-button"
                  onClick={() => setPreviewOpen(false)}
                >
                  Use this template
                </button>
              </footer>
            </section>
          </div>
        )}
      </section>
    </div>
  );

  return createPortal(modalContent, document.body);
}

/* -------------------------------------------------------------------------- */
/*                              Small components                              */
/* -------------------------------------------------------------------------- */

function SectionTitle({ title }) {
  return <h3 className="invoice-builder__section-title">{title}</h3>;
}

function Field({ label, children, wide = false }) {
  return (
    <label
      className={
        wide
          ? "invoice-builder__field invoice-builder__field--wide"
          : "invoice-builder__field"
      }
    >
      <span>{label}</span>
      {children}
    </label>
  );
}

function TemplateCard({ template, selected, data, onClick }) {
  const html = useMemo(
    () =>
      renderInvoiceTemplateHtml({
        templateId: template.id,
        data,
        isThumbnail: true,
      }),
    [template.id, data],
  );

  return (
    <button
      type="button"
      className={
        selected
          ? "invoice-template-card invoice-template-card--selected"
          : "invoice-template-card"
      }
      onClick={onClick}
    >
      <strong>{template.label}</strong>

      <div className="invoice-template-card__preview">
        <iframe
          title={`${template.label} invoice template`}
          srcDoc={html}
          tabIndex="-1"
        />
      </div>

      <small>Click to preview</small>
    </button>
  );
}
